import { useContext, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { Context } from "../../context/context";
import useOnClickOutside from "../../hooks/useOnClickOutside";
import Avatar from "../avatar/avatar.component";
import { LogoutIcon, MenuIcon, UserIcon } from "../ui/icons";

/**
 * Top bar. Carries the page title and the user menu, plus the drawer trigger
 * below the `nav` breakpoint.
 */
const Header = ({ title, subtitle, showMenuButton, onOpenMenu }) => {
  const { state } = useContext(Context);
  const { user, scrollPosition } = state;

  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  useOnClickOutside(menuRef, () => setMenuOpen(false));

  const navigate = useNavigate();
  const { deviceId } = useParams();

  return (
    <header
      className={`sticky top-0 z-20 flex h-16 flex-shrink-0 items-center gap-3 bg-white px-4 transition-shadow nav:px-8 ${
        scrollPosition > 10 ? "border-b border-gray-100 shadow-sm" : "border-b border-gray-100"
      }`}
    >
      {showMenuButton && (
        <button
          onClick={onOpenMenu}
          aria-label="Open menu"
          className="-ml-1 flex h-9 w-9 items-center justify-center rounded-lg text-gray-600 transition hover:bg-gray-100"
        >
          <MenuIcon className="h-5 w-5" />
        </button>
      )}

      <div className="min-w-0 flex-1">
        <p className="truncate text-base font-bold text-primary-900">{title}</p>
        {subtitle && <p className="truncate text-xs text-gray-500">{subtitle}</p>}
      </div>

      <div className="relative flex-shrink-0" ref={menuRef}>
        <button
          onClick={() => setMenuOpen((v) => !v)}
          aria-label="Account menu"
          aria-expanded={menuOpen}
          className="block rounded-full"
        >
          <Avatar src={user?.photoURL} size="sm">
            {user?.displayName}
          </Avatar>
        </button>

        {menuOpen && (
          <div className="absolute right-0 z-30 mt-2 w-52 overflow-hidden rounded-xl border border-hairline bg-white py-1 shadow-pop">
            <div className="border-b border-gray-100 px-4 py-2.5">
              <p className="truncate text-sm font-semibold text-primary-900">{user?.displayName}</p>
              <p className="truncate text-[11px] text-gray-500">{user?.email}</p>
            </div>
            <button
              onClick={() => {
                setMenuOpen(false);
                navigate("/profile");
              }}
              className="flex w-full items-center gap-2.5 px-4 py-2.5 text-left text-sm text-gray-600 transition hover:bg-gray-50"
            >
              <UserIcon className="h-4 w-4 text-gray-400" />
              Profile
            </button>
            <button
              onClick={() => {
                setMenuOpen(false);
                navigate("/signout");
              }}
              className="flex w-full items-center gap-2.5 px-4 py-2.5 text-left text-sm text-gray-600 transition hover:bg-gray-50"
            >
              <LogoutIcon className="h-4 w-4 text-gray-400" />
              Sign out
            </button>
          </div>
        )}
      </div>

      {/* deviceId is read so the header re-renders on device switch */}
      <span className="hidden" data-device={deviceId} />
    </header>
  );
};

export default Header;
